import * as THREE from 'three';
import { MEASURE } from '../config.js';

/**
 * 2点クリックで実寸を測る。
 * Scaniverse / Polycam のスキャンは ARKit/ARCore 由来でメートル実寸なので、
 * スプラットのワールド距離をそのまま m として扱える。
 */
export function createMeasureTool({ scene, camera, canvas, space, onChange }) {
  const raycaster = new THREE.Raycaster();
  const group = new THREE.Group();
  group.visible = false;
  scene.add(group);

  /** 確定した点の配列（最大2点）。常に新しい配列に差し替える */
  let points = [];
  let enabled = false;

  function setEnabled(next) {
    enabled = Boolean(next);
    group.visible = enabled;
    if (!enabled) clear();
    canvas.style.cursor = enabled ? 'crosshair' : '';
  }

  function clear() {
    points = [];
    redraw();
    notify();
  }

  function handleClick(event) {
    if (!enabled) return;
    const hit = pick(event);
    if (hit === null) {
      // 何も無い空間や薄すぎる面をクリックした場合。黙って無視すると
      // 「反応しない」と受け取られるため、状態として通知する。
      notify({ missed: true });
      return;
    }
    points = points.length >= 2 ? [hit] : [...points, hit];
    redraw();
    notify();
  }

  function pick(event) {
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const ndc = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );
    raycaster.setFromCamera(ndc, camera);
    const intersects = [];
    space.raycast(raycaster, intersects);
    if (intersects.length === 0) return null;
    const nearest = intersects.reduce((a, b) => (a.distance <= b.distance ? a : b));
    return nearest.point.clone();
  }

  function redraw() {
    disposeChildren(group);
    for (const p of points) group.add(makeMarker(p));
    if (points.length === 2) group.add(makeLine(points[0], points[1]));
  }

  function notify({ missed = false } = {}) {
    if (typeof onChange !== 'function') return;
    const distance = points.length === 2 ? points[0].distanceTo(points[1]) : null;
    onChange({ count: points.length, distance, missed });
  }

  canvas.addEventListener('click', handleClick);

  return {
    setEnabled,
    clear,
    get enabled() { return enabled; },
    dispose() {
      canvas.removeEventListener('click', handleClick);
      disposeChildren(group);
      scene.remove(group);
    },
  };
}

function makeMarker(position) {
  const geometry = new THREE.SphereGeometry(MEASURE.markerRadius, 16, 12);
  const material = new THREE.MeshBasicMaterial({ color: MEASURE.markerColor, depthTest: false });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.copy(position);
  mesh.renderOrder = 999;
  return mesh;
}

function makeLine(a, b) {
  const geometry = new THREE.BufferGeometry().setFromPoints([a, b]);
  const material = new THREE.LineBasicMaterial({ color: MEASURE.lineColor, depthTest: false });
  const line = new THREE.Line(geometry, material);
  line.renderOrder = 999;
  return line;
}

function disposeChildren(group) {
  for (const child of [...group.children]) {
    group.remove(child);
    child.geometry?.dispose();
    child.material?.dispose();
  }
}
